import React, { useEffect } from "react";
import DollarInputText from "../../controls/DollarInputText";
import type { NumericInputChangeEvent } from "../../controls/InputNumberOnly";
import { updateObjectInArray } from "../../../shared/utility";

// TypeScript interfaces
export interface PriceListItem {
  id: string | number;
  name: string;
  availability: boolean;
  price: string | number;
}

interface ProductPriceListProps {
  priceList: PriceListItem[];
  actionCall: () => void;
  setPriceList: (priceList: PriceListItem[]) => void;
}

export default function ProductPriceList(props: ProductPriceListProps) {
  const { priceList, actionCall, setPriceList } = props;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    actionCall();
  };

  useEffect(() => {
    if (priceList) {
      // Any initialization logic can go here
    }
  }, []);

  const handleItemChange = (name: string, newValue: string | number | boolean, index: number) => {
    const copyPriceList = [...priceList];
    const singleItem = copyPriceList[index];

    let singleItemBeingEdited: PriceListItem = {
      ...singleItem,
      [name]: newValue,
    };

    if (name === "price") {
      singleItemBeingEdited = {
        ...singleItemBeingEdited,
        availability: true,
      };
    }

    const payload = {
      index: index,
      item: singleItemBeingEdited,
    };
    const newPriceList = updateObjectInArray(copyPriceList, payload);
    setPriceList(newPriceList);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Price List Management</h3>
          <p className="text-sm text-gray-600 mt-1">
            Configure pricing and availability for different customer groups
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Table */}
            <div className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Customer Group
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Available
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Price
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {priceList.map((group, index) => (
                      <tr
                        key={group.id}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="items-center">
                            <div className="text-sm font-medium text-gray-900">{group.name}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <input
                              id={`availability-${group.id}`}
                              name="availability"
                              type="checkbox"
                              checked={group.availability}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded transition-colors duration-150"
                              onChange={(e) =>
                                handleItemChange("availability", e.target.checked, index)
                              }
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <DollarInputText
                            name="unit_price"
                            onChange={(e: NumericInputChangeEvent) =>
                              handleItemChange("price", e.target.value, index)
                            }
                            value={group.price === "" ? null : Number(group.price)}
                            labelOn={false}
                            className="w-full"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button
                type="submit"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                Save Price List
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
